import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { ApiError } from "../lib/api";
import { Brand } from "../components/Brand";
import { GoogleButton } from "../components/GoogleButton";

export default function Register() {
  const { signUp } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password.length > 72) {
      setError("Password must be 72 characters or fewer.");
      return;
    }
    setLoading(true);
    try {
      await signUp(email.trim(), password, name.trim() || undefined);
      nav("/library", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6 py-16">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <Brand className="text-2xl" />
          <p className="mt-6 font-body italic text-ink-300">
            A place for the books you haven't yet written.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-sm border border-ink-700 bg-ink-800/60 backdrop-blur-sm p-8"
        >
          <div className="eyebrow mb-6">Create your account</div>

          <label className="field-label" htmlFor="name">Name (optional)</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="field mb-5"
            placeholder="How you'd like to be addressed"
          />

          <label className="field-label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field mb-5"
            placeholder="you@example.com"
          />

          <label className="field-label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field mb-6"
            placeholder="At least 8 characters"
          />

          {error ? (
            <div className="mb-5 text-sm text-oxblood" role="alert">{error}</div>
          ) : null}

          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? "Creating…" : "Begin"}
          </button>

          <GoogleButton label="signup_with" />

          <p className="mt-6 text-center text-sm text-ink-300">
            Already have an account?{" "}
            <Link to="/login" className="text-gold-500 hover:text-gold-300">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
